import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { getStateErrorsForLineChart } from '../../sources/dashboard';
import type { StateErrorsLineChartDataWithFilters, TimeFilters } from '../../types/dashboard';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

interface InsightsProviderContext {
    usageDateFilter: TimeFilters;
    setUsageDateFilter: (filter: TimeFilters) => void;
    errorsDateFilter: TimeFilters;
    setErrorsDateFilter: (filter: TimeFilters) => void;
    stateErrorsLineChartData: StateErrorsLineChartDataWithFilters;
    customUsageToDate: Date | null;
    setCustomUsageToDate: (toDate: Date | null) => void;
    customErrorsToDate: Date;
    setCustomErrorsToDate: (toDate: Date) => void;
    errorsFlowNameFilter: string;
    setErrorsFlowNameFilter: (filter: string) => void;
    errorsSearchFilter: string;
    setErrorsSearchFilter: (filter: string) => void;
}

interface InsightsProviderProps {
    children: ReactNode;
}

const Context = createContext<InsightsProviderContext | undefined>(undefined);

const InsightsProvider = ({ children }: InsightsProviderProps) => {
    const [usageDateFilter, setUsageDateFilter] = useState<TimeFilters>('day');
    const [errorsDateFilter, setErrorsDateFilter] = useState<TimeFilters>('day');
    // We can't set the customToDate to now unless the user requests it otherwise the toDate will begin to drift out of sync if the page is left open for a while.
    // null is later ignored and a new instance of now is generated to prevent this.
    const [customUsageToDate, setCustomUsageToDate] = useState<Date | null>(null);
    const [customErrorsToDate, setCustomErrorsToDate] = useState(new Date());
    const [errorsFlowNameFilter, setErrorsFlowNameFilter] = useState('');
    const [errorsSearchFilter, setErrorsSearchFilter] = useState('');

    const dispatchNotification = useNotificationDispatch();

    const [stateErrorsLineChartData, setStateErrorsLineChartData] =
        useState<StateErrorsLineChartDataWithFilters>({
            stateErrorsData: [],
            stateErrorsTotal: 0,
            dateFilter: 'day',
            customToDate: new Date(),
        });

    useEffect(() => {
        getStateErrorsForLineChart(
            errorsDateFilter,
            customErrorsToDate,
            errorsSearchFilter,
            errorsFlowNameFilter,
        )
            .then((response) =>
                setStateErrorsLineChartData({
                    stateErrorsData: response.stateErrorsData,
                    stateErrorsTotal: response.stateErrorsTotal,
                    dateFilter: errorsDateFilter,
                    customToDate: customErrorsToDate,
                }),
            )
            .catch((error) => {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            });
    }, [
        errorsDateFilter,
        dispatchNotification,
        customErrorsToDate,
        errorsSearchFilter,
        errorsFlowNameFilter,
    ]);

    const contextValue: InsightsProviderContext = {
        usageDateFilter,
        setUsageDateFilter,
        errorsDateFilter,
        setErrorsDateFilter,
        stateErrorsLineChartData,
        customUsageToDate,
        setCustomUsageToDate,
        customErrorsToDate,
        setCustomErrorsToDate,
        errorsFlowNameFilter,
        setErrorsFlowNameFilter,
        errorsSearchFilter,
        setErrorsSearchFilter,
    };

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

const useInsights = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useInsights must be used within an InsightsProvider');
    }
    return context;
};

export { InsightsProvider, useInsights };
